import { Link } from "react-router-dom";
import { Globe, FileText } from "lucide-react";

const TermsPage = () => {
  return (
    <div className="min-h-screen pb-12 bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Globe className="w-6 h-6 text-primary" />
            <span className="font-heading text-xl font-bold tracking-tight text-foreground">MeteoTranslate</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/translate" className="nav-link text-sm font-medium">Translate</Link>
            <Link to="/terminology" className="nav-link text-sm font-medium">Terminology</Link>
            <Link to="/metrics" className="nav-link text-sm font-medium">Metrics</Link>
            <Link to="/history" className="nav-link text-sm font-medium">History</Link>
            <Link to="/settings" className="nav-link text-sm font-medium">Settings</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 pt-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-5 h-5 text-primary" />
            <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">
              Terms of Service
            </h1>
          </div>
          <p className="text-muted-foreground">
            Last updated: January 28, 2026
          </p>
        </div>

        {/* Terms Content */}
        <div className="prose prose-slate max-w-none">
          <div className="bg-muted/50 p-6 rounded-lg border border-border mb-8">
            <p className="text-sm text-muted-foreground mb-0">
              <strong>MeteoTranslate</strong> is powered by <strong>ALTAM™ Operating Software</strong>, 
              a product of <strong>Pecaro Consulting Inc.</strong>
            </p>
          </div>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-bold text-foreground mb-4">1. Agreement to Terms</h2>
            <p className="text-muted-foreground mb-4">
              ALTAM™ Operating Services ("ALTAM™") is a software platform operated by Pecaro Consulting Inc. 
              ("Company", "we", "us", "our"). By accessing or using ALTAM™, you ("Customer", "you", "your") 
              agree to be bound by these Terms of Service ("Terms").
            </p>
            <p className="text-muted-foreground">
              If you do not agree to these Terms, you must not access or use ALTAM™ or any of its associated 
              services, including MeteoTranslate.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-bold text-foreground mb-4">2. Description of Service</h2>
            <p className="text-muted-foreground mb-4">
              MeteoTranslate is an AI-powered meteorological translation service that provides:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Bi-directional translation between Canadian English (EN-CA) and Canadian French (FR-CA)</li>
              <li>Specialized meteorological terminology locking</li>
              <li>Machine-to-machine (M2M) API access</li>
              <li>Translation quality metrics and audit trails</li>
              <li>Batch processing capabilities</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-bold text-foreground mb-4">3. Use of Service</h2>
            <p className="text-muted-foreground mb-4">
              You agree to use ALTAM™ and MeteoTranslate only for lawful purposes and in accordance with 
              these Terms. You agree not to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Use the service in any way that violates applicable laws or regulations</li>
              <li>Attempt to gain unauthorized access to any part of the service</li>
              <li>Interfere with or disrupt the service or servers</li>
              <li>Use the service to transmit harmful or malicious content</li>
              <li>Reverse engineer, decompile, or disassemble any part of the service</li>
              <li>Use the service to infringe upon intellectual property rights of others</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-bold text-foreground mb-4">4. API Keys and Authentication</h2>
            <p className="text-muted-foreground mb-4">
              If you are provided with API keys for programmatic access:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>You are responsible for maintaining the confidentiality of your API keys</li>
              <li>You must not share your API keys with unauthorized parties</li>
              <li>You are responsible for all activities that occur using your API keys</li>
              <li>You must notify us immediately of any unauthorized use</li>
              <li>We reserve the right to revoke API keys at any time</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-bold text-foreground mb-4">5. Data and Privacy</h2>
            <p className="text-muted-foreground mb-4">
              By using MeteoTranslate, you acknowledge that:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Translations and associated metadata may be logged for audit trail purposes</li>
              <li>We may use aggregated, anonymized data for service improvement</li>
              <li>You are responsible for ensuring you have the right to submit content for translation</li>
              <li>Sensitive or classified information should not be submitted without appropriate authorization</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-bold text-foreground mb-4">6. Intellectual Property</h2>
            <p className="text-muted-foreground mb-4">
              ALTAM™, MeteoTranslate, and all associated trademarks, logos, and content are the property 
              of Pecaro Consulting Inc. or its licensors. You retain ownership of any content you submit 
              for translation, but grant us a limited license to process such content as necessary to 
              provide the service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-bold text-foreground mb-4">7. Disclaimer of Warranties</h2>
            <p className="text-muted-foreground mb-4">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
              EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Warranties of merchantability or fitness for a particular purpose</li>
              <li>Warranties that the service will be uninterrupted or error-free</li>
              <li>Warranties regarding the accuracy of translations</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              While we strive for high translation accuracy, automated translations should be reviewed 
              for critical applications. Confidence scores are provided as guidance only.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-bold text-foreground mb-4">8. Limitation of Liability</h2>
            <p className="text-muted-foreground">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, PECARO CONSULTING INC. AND ITS AFFILIATES SHALL NOT 
              BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY 
              LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, 
              USE, GOODWILL, OR OTHER INTANGIBLE LOSSES RESULTING FROM YOUR USE OF THE SERVICE.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-bold text-foreground mb-4">9. Indemnification</h2>
            <p className="text-muted-foreground">
              You agree to indemnify and hold harmless Pecaro Consulting Inc. and its officers, directors, 
              employees, and agents from any claims, damages, losses, liabilities, and expenses arising 
              out of your use of the service or violation of these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-bold text-foreground mb-4">10. Modifications to Terms</h2>
            <p className="text-muted-foreground">
              We reserve the right to modify these Terms at any time. We will provide notice of material 
              changes by posting the updated Terms on this page with a new "Last updated" date. Your 
              continued use of the service after such modifications constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-bold text-foreground mb-4">11. Governing Law</h2>
            <p className="text-muted-foreground">
              These Terms shall be governed by and construed in accordance with the laws of the Province 
              of Ontario and the federal laws of Canada applicable therein, without regard to conflict 
              of law principles.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-bold text-foreground mb-4">12. Contact Information</h2>
            <p className="text-muted-foreground mb-4">
              For questions about these Terms or the service, please contact:
            </p>
            <div className="bg-muted/50 p-4 rounded-lg border border-border">
              <p className="text-foreground font-medium">Pecaro Consulting Inc.</p>
              <p className="text-muted-foreground text-sm">Operating ALTAM™ Services</p>
            </div>
          </section>

          <section className="mt-12 pt-8 border-t border-border">
            <p className="text-sm text-muted-foreground text-center">
              © {new Date().getFullYear()} Pecaro Consulting Inc. All rights reserved.<br />
              ALTAM™ is a trademark of Pecaro Consulting Inc.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
